import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);
const REFETCH_INTERVAL_MS = 5 * 60 * 1000;

const TIER_ORDER = { none: 0, assistent: 1, bureau: 2 };

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [tier, setTier] = useState('none');
  const [usageSummary, setUsageSummary] = useState({});
  const [tierOverride, setTierOverride] = useState(null);
  const [resetsAt, setResetsAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('tripdash_supabase_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/account/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTier(data.tier || 'none');
      setUsageSummary(data.usageSummary || {});
      setTierOverride(data.tierOverride || null);
      setResetsAt(data.resetsAt || null);
    } catch {}
    finally { setIsLoading(false); }
  }, [user]);

  useEffect(() => {
    if (user) {
      setIsLoading(true);
      fetchSubscription();
      const interval = setInterval(fetchSubscription, REFETCH_INTERVAL_MS);
      return () => clearInterval(interval);
    } else {
      setTier('none');
      setUsageSummary({});
      setIsLoading(false);
    }
  }, [user, fetchSubscription]);

  const hasAccess = useCallback((requiredTier) => {
    return (TIER_ORDER[tier] || 0) >= (TIER_ORDER[requiredTier] || 0);
  }, [tier]);

  const getCapStatus = useCallback((feature) => {
    const info = usageSummary[feature];
    if (!info || info.cap === null) return { used: null, cap: null, percentage: 0, isAtCap: false };
    const percentage = info.cap > 0 ? Math.round((info.used / info.cap) * 100) : 0;
    return { used: info.used, cap: info.cap, percentage, isAtCap: info.used >= info.cap };
  }, [usageSummary]);

  return (
    <SubscriptionContext.Provider value={{
      tier, tierOverride, usageSummary, resetsAt, isLoading,
      hasAccess, getCapStatus, refreshSubscription: fetchSubscription,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
}
